import React, { useState } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { ModalForm } from '../../../../components/generic';
import capaActionConfig from '../actions/capaActionConfig';
import { API_BASE_URL } from '../../../../config/apiConfig';
import { useFlexibleList, useDeleteEntity } from '../../../../hooks/useSWR';

const CapaActionsTab = ({ 
  selectedRecord,
  onActionUpdate // Callback to refresh CAPA data after action changes
}) => {
  const { theme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  // Use SWR to fetch CAPA actions
  const { data: capaActions = [], isLoading, error, mutate: refreshActions } = useFlexibleList(
    selectedRecord?.id ? `/capa-action/by-capa/${selectedRecord.id}` : null
  );

  // SWR hook for deletion
  const deleteEntity = useDeleteEntity('/capa-action');

  const handleAddAction = () => {
    setEditingAction(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingAction(null);
  };

  const handleActionSuccess = (result) => {
    // Refresh the CAPA actions using SWR
    refreshActions();
    
    // Also refresh the CAPA data to show updated actions count
    if (onActionUpdate) {
      onActionUpdate();
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Are you sure you want to delete this action?')) {
      return;
    }

    try {
      // Use SWR hook for deletion
      await deleteEntity(actionId);
      
      // Refresh the CAPA actions using SWR
      refreshActions();
      
      // Also refresh the CAPA data
      if (onActionUpdate) {
        onActionUpdate();
      }
    } catch (error) {
      console.error('Error deleting action:', error);
      alert('Failed to delete action');
    }
  };

  return (
    <>
      <div 
        className="p-4 flex-1 overflow-y-auto advanced-table-scroll space-y-4"
        style={{ 
          scrollbarWidth: 'thin',
          minHeight: 0,
          maxHeight: '100%'
        }}
      >
        {/* Header with Add Button */}
        <div className="flex items-center justify-end">
          <button
            onClick={handleAddAction}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
              theme.colors.primary,
              theme.text.inverse,
              "hover:opacity-90"
            )}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Action
          </button>
        </div>

        {/* CAPA Actions Table */}
        <div className="space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className={cn("text-center py-8", theme.text.muted)}>
              <div className="mb-4">
                <svg className="animate-spin w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <div className="text-sm">Loading actions...</div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={cn("text-center py-8", theme.text.muted)}>
              <div className="mb-4">
                <svg className="w-8 h-8 mx-auto text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-sm text-red-600 mb-2">Error loading actions</div>
              <div className="text-xs text-gray-500">{error.message || error}</div>
              <button
                onClick={refreshActions}
                className={cn("mt-2 px-3 py-1 text-xs rounded border", theme.border.light, theme.text.secondary)}
              >
                Retry
              </button>
            </div>
          )}

          {/* Data List */}
          {!isLoading && !error && capaActions.length > 0 ? (
            <div className="space-y-3">
              {capaActions.map((item, index) => (
                <div key={item.id || index} className={cn(
                  "p-4 rounded-lg border transition-colors duration-200",
                  theme.border.subtle, 
                  theme.colors.background,
                  "hover:border-opacity-60"
                )}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      {/* Header with title */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        {/* Main title/description field */}
                        <div className={cn("text-sm font-medium", theme.text.primary)}>
                          {item.description || item.name || item.title || `Action ${index + 1}`}
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditAction(item)}
                            className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", theme.text.secondary)}
                            title="Edit action"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteAction(item.id || item)}
                            className={cn("p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400")}
                            title="Delete action"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Data fields grid - Simple 2-column layout */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        
                        {/* Column 1 */}
                        <div className="space-y-2">
                          {/* Type */}
                          <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                            <span className="font-medium">Type:</span>
                            <span>{item.type?.name || 'Not set'}</span>
                          </div>
                          
                          {/* Assigned To */}
                          <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                            <span className="font-medium">Assigned To:</span>
                            <span>{item.user?.userDisplayName || 'Not set'}</span>
                          </div>
                          
                          {/* Due Date */}
                          <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                            <span className="font-medium">Due Date:</span>
                            <span>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'Not set'}</span>
                          </div>
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-2">
                          {/* Priority */}
                          <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                            <span className="font-medium">Priority:</span>
                            <span>{item.priority?.name || 'Not set'}</span>
                          </div>
                          
                          {/* Status */}
                          <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                            <span className="font-medium">Status:</span>
                            <span>{item.status?.name || 'Not set'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Status badges (right side) */}
                    <div className="flex-shrink-0 flex flex-col gap-2">
                      {/* Status badge */}
                      {item.status && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.status.name || item.status}
                        </span>
                      )}
                      

                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !isLoading && !error && (
              <div className={cn("text-center py-8", theme.text.muted)}>
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div className="text-sm font-medium mb-2">No actions found</div>
                <div className="text-xs">Click 'Add Action' to create the first action for this CAPA</div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Modal Form */}
      <ModalForm
        isOpen={isModalOpen}
        onClose={handleModalClose}
        config={capaActionConfig}
        initialData={editingAction}
        mode={modalMode}
        onSuccess={handleActionSuccess}
        parentId={selectedRecord?.id} // Pass the CAPA ID as parent
      />
    </>
  );
};

export default CapaActionsTab;
